// hotel/F&B/printingService.js
class PrintingService {
    constructor() {
        this.printQueue = [];
        this.isPrinting = false;
    }

    // Helper to get current staff name
    getCurrentStaffName() {
        try {
            const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
            return storedProfile.first_name || storedProfile.name || 'Staff';
        } catch (error) {
            console.error('Error getting staff name:', error);
            return 'Staff';
        }
    }

    // Format and print KOT (Kitchen Order Ticket) - Enhanced for thermal printer legibility with staff details
    printKOT(ticketData) {
        const hotelName = localStorage.getItem('hotel_name') || 'Frankville Hotels';
        const date = new Date().toLocaleString();
        const staffName = this.getCurrentStaffName();
        
        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>KOT - ${ticketData.order_number}</title>
                <style>
                    @media print {
                        @page { 
                            margin: 0; 
                            size: 80mm auto; 
                        }
                        body { 
                            font-family: 'Courier New', monospace;
                            font-size: 13px;
                            font-weight: bold;
                            margin: 0;
                            padding: 8px;
                            width: 80mm;
                        }
                        * {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                    }
                    * { 
                        margin: 0; 
                        padding: 0; 
                        box-sizing: border-box; 
                    }
                    body {
                        font-family: 'Courier New', monospace;
                        font-size: 13px;
                        font-weight: bold;
                        padding: 8px;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .header { 
                        text-align: center; 
                        margin-bottom: 10px; 
                    }
                    .header strong {
                        font-weight: 900;
                        font-size: 15px;
                    }
                    .title { 
                        font-size: 18px; 
                        font-weight: 900; 
                        margin: 5px 0; 
                    }
                    .kot-title { 
                        color: #ff6b35; 
                        font-weight: 900;
                    }
                    .divider { 
                        border-top: 2px dashed #000; 
                        margin: 8px 0; 
                    }
                    .item-row { 
                        display: flex; 
                        justify-content: space-between; 
                        margin: 4px 0;
                        font-weight: bold;
                        font-size: 13px;
                    }
                    .additional { 
                        background: #fff3e0; 
                        padding: 5px; 
                        margin: 5px 0; 
                        border-left: 4px solid #ff6b35;
                        font-weight: bold;
                    }
                    .footer { 
                        text-align: center; 
                        margin-top: 10px; 
                        font-size: 11px;
                        font-weight: bold;
                    }
                    .instruction { 
                        background: #f0f0f0; 
                        padding: 5px; 
                        margin: 5px 0;
                        font-weight: bold;
                    }
                    .order-detail {
                        font-weight: bold;
                        font-size: 13px;
                        margin: 3px 0;
                    }
                    .order-detail strong {
                        font-weight: 900;
                    }
                    .item-header {
                        display: flex;
                        justify-content: space-between;
                        font-weight: 900;
                        font-size: 14px;
                        border-bottom: 1px solid #000;
                        padding-bottom: 5px;
                        margin-bottom: 5px;
                    }
                    .timestamp {
                        font-weight: bold;
                        font-size: 12px;
                    }
                    .staff-info {
                        font-weight: bold;
                        font-size: 12px;
                        margin-top: 5px;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div><strong style="font-size:16px;">${hotelName}</strong></div>
                    <div class="title kot-title">🔪 KITCHEN ORDER TICKET (KOT)</div>
                    <div class="divider"></div>
                </div>
                
                <div>
                    <div class="order-detail"><strong>Order #:</strong> ${ticketData.order_number}</div>
                    ${ticketData.table_number ? `<div class="order-detail"><strong>Table:</strong> ${ticketData.table_number}</div>` : ''}
                    <div class="order-detail"><strong>Time:</strong> ${date}</div>
                    ${ticketData.is_additional ? '<div class="additional"><strong>⚠️ ADDITIONAL ORDER ⚠️</strong></div>' : ''}
                </div>
                
                <div class="divider"></div>
                
                <div>
                    <div class="item-header">
                        <span>QTY</span>
                        <span>ITEM</span>
                        <span>TOTAL</span>
                    </div>
                    ${ticketData.items.map(item => `
                        <div class="item-row">
                            <span style="font-weight:900;">${item.quantity}x</span>
                            <span style="font-weight:bold;">${item.name.substring(0, 25)}</span>
                            <span style="font-weight:900;">₦${item.total.toFixed(2)}</span>
                        </div>
                    `).join('')}
                </div>
                
                <div class="divider"></div>
                
                ${ticketData.special_instructions ? `
                    <div class="instruction">
                        <strong style="font-size:14px;">📝 Special Instructions:</strong><br>
                        <span style="font-weight:bold;">${ticketData.special_instructions}</span>
                    </div>
                    <div class="divider"></div>
                ` : ''}
                
                <div class="footer">
                    🔥 SEND TO KITCHEN 🔥<br>
                    <span class="timestamp">${new Date().toLocaleTimeString()}</span><br>
                    <span class="staff-info">Staff: ${staffName}</span>
                </div>
                
                <script>
                    window.onload = function() {
                        window.print();
                        setTimeout(function() { window.close(); }, 800);
                    }
                </script>
            </body>
            </html>
        `;
        
        this._print(printContent);
    }

    // Format and print BOT (Beverage Order Ticket) - Enhanced for thermal printer legibility with staff details
    printBOT(ticketData) {
        const hotelName = localStorage.getItem('hotel_name') || 'Frankville Hotels';
        const date = new Date().toLocaleString();
        const staffName = this.getCurrentStaffName();
        
        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>BOT - ${ticketData.order_number}</title>
                <style>
                    @media print {
                        @page { 
                            margin: 0; 
                            size: 80mm auto; 
                        }
                        body { 
                            font-family: 'Courier New', monospace;
                            font-size: 13px;
                            font-weight: bold;
                            margin: 0;
                            padding: 8px;
                            width: 80mm;
                        }
                        * {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                    }
                    * { 
                        margin: 0; 
                        padding: 0; 
                        box-sizing: border-box; 
                    }
                    body {
                        font-family: 'Courier New', monospace;
                        font-size: 13px;
                        font-weight: bold;
                        padding: 8px;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .header { 
                        text-align: center; 
                        margin-bottom: 10px; 
                    }
                    .header strong {
                        font-weight: 900;
                        font-size: 15px;
                    }
                    .title { 
                        font-size: 18px; 
                        font-weight: 900; 
                        margin: 5px 0; 
                    }
                    .bot-title { 
                        color: #2196f3; 
                        font-weight: 900;
                    }
                    .divider { 
                        border-top: 2px dashed #000; 
                        margin: 8px 0; 
                    }
                    .item-row { 
                        display: flex; 
                        justify-content: space-between; 
                        margin: 4px 0;
                        font-weight: bold;
                        font-size: 13px;
                    }
                    .additional { 
                        background: #e3f2fd; 
                        padding: 5px; 
                        margin: 5px 0; 
                        border-left: 4px solid #2196f3;
                        font-weight: bold;
                    }
                    .footer { 
                        text-align: center; 
                        margin-top: 10px; 
                        font-size: 11px;
                        font-weight: bold;
                    }
                    .instruction { 
                        background: #f0f0f0; 
                        padding: 5px; 
                        margin: 5px 0;
                        font-weight: bold;
                    }
                    .order-detail {
                        font-weight: bold;
                        font-size: 13px;
                        margin: 3px 0;
                    }
                    .order-detail strong {
                        font-weight: 900;
                    }
                    .item-header {
                        display: flex;
                        justify-content: space-between;
                        font-weight: 900;
                        font-size: 14px;
                        border-bottom: 1px solid #000;
                        padding-bottom: 5px;
                        margin-bottom: 5px;
                    }
                    .timestamp {
                        font-weight: bold;
                        font-size: 12px;
                    }
                    .staff-info {
                        font-weight: bold;
                        font-size: 12px;
                        margin-top: 5px;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div><strong style="font-size:16px;">${hotelName}</strong></div>
                    <div class="title bot-title">🍹 BAR ORDER TICKET (BOT)</div>
                    <div class="divider"></div>
                </div>
                
                <div>
                    <div class="order-detail"><strong>Order #:</strong> ${ticketData.order_number}</div>
                    ${ticketData.table_number ? `<div class="order-detail"><strong>Table:</strong> ${ticketData.table_number}</div>` : ''}
                    <div class="order-detail"><strong>Time:</strong> ${date}</div>
                    ${ticketData.is_additional ? '<div class="additional"><strong>⚠️ ADDITIONAL ORDER ⚠️</strong></div>' : ''}
                </div>
                
                <div class="divider"></div>
                
                <div>
                    <div class="item-header">
                        <span>QTY</span>
                        <span>ITEM</span>
                        <span>TOTAL</span>
                    </div>
                    ${ticketData.items.map(item => `
                        <div class="item-row">
                            <span style="font-weight:900;">${item.quantity}x</span>
                            <span style="font-weight:bold;">${item.name.substring(0, 25)}</span>
                            <span style="font-weight:900;">₦${item.total.toFixed(2)}</span>
                        </div>
                    `).join('')}
                </div>
                
                <div class="divider"></div>
                
                ${ticketData.special_instructions ? `
                    <div class="instruction">
                        <strong style="font-size:14px;">📝 Special Instructions:</strong><br>
                        <span style="font-weight:bold;">${ticketData.special_instructions}</span>
                    </div>
                    <div class="divider"></div>
                ` : ''}
                
                <div class="footer">
                    🍸 PREPARE BEVERAGES 🍺<br>
                    <span class="timestamp">${new Date().toLocaleTimeString()}</span><br>
                    <span class="staff-info">Served by: ${staffName}</span>
                </div>
                
                <script>
                    window.onload = function() {
                        window.print();
                        setTimeout(function() { window.close(); }, 800);
                    }
                </script>
            </body>
            </html>
        `;
        
        this._print(printContent);
    }

    // Enhanced receipt print for thermal printer
    printReceipt(orderData) {
        const hotelName = localStorage.getItem('hotel_name') || 'Frankville Hotels';
        const date = new Date().toLocaleString();
        const staffName = this.getCurrentStaffName();
        
        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Receipt - ${orderData.order_number}</title>
                <style>
                    @media print {
                        @page { 
                            margin: 0; 
                            size: 80mm auto; 
                        }
                        body { 
                            font-family: 'Courier New', monospace;
                            font-size: 13px;
                            font-weight: bold;
                            margin: 0;
                            padding: 8px;
                            width: 80mm;
                        }
                        * {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                    }
                    * { 
                        margin: 0; 
                        padding: 0; 
                        box-sizing: border-box; 
                    }
                    body {
                        font-family: 'Courier New', monospace;
                        font-size: 13px;
                        font-weight: bold;
                        padding: 8px;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .header { 
                        text-align: center; 
                        margin-bottom: 12px; 
                    }
                    .header strong {
                        font-weight: 900;
                        font-size: 16px;
                    }
                    .title { 
                        font-size: 20px; 
                        font-weight: 900; 
                        margin: 5px 0; 
                    }
                    .divider { 
                        border-top: 2px dashed #000; 
                        margin: 8px 0; 
                    }
                    .divider-thick { 
                        border-top: 3px solid #000; 
                        margin: 8px 0; 
                    }
                    .item-row { 
                        display: flex; 
                        justify-content: space-between; 
                        margin: 4px 0;
                        font-weight: bold;
                        font-size: 13px;
                    }
                    .footer { 
                        text-align: center; 
                        margin-top: 12px; 
                        font-size: 12px;
                        font-weight: bold;
                    }
                    .order-detail {
                        font-weight: bold;
                        font-size: 13px;
                        margin: 3px 0;
                    }
                    .order-detail strong {
                        font-weight: 900;
                    }
                    .item-header {
                        display: flex;
                        justify-content: space-between;
                        font-weight: 900;
                        font-size: 14px;
                        border-bottom: 2px solid #000;
                        padding-bottom: 5px;
                        margin-bottom: 5px;
                    }
                    .total-row {
                        display: flex;
                        justify-content: space-between;
                        font-weight: 900;
                        font-size: 15px;
                        margin: 4px 0;
                    }
                    .grand-total {
                        display: flex;
                        justify-content: space-between;
                        font-weight: 900;
                        font-size: 18px;
                        margin: 8px 0;
                        border-top: 3px solid #000;
                        padding-top: 8px;
                    }
                    .thank-you {
                        text-align: center;
                        font-size: 16px;
                        font-weight: 900;
                        margin: 12px 0;
                    }
                    .timestamp {
                        font-weight: bold;
                        font-size: 12px;
                    }
                    .status-badge {
                        text-align: center;
                        font-weight: 900;
                        font-size: 14px;
                        padding: 5px;
                        background: #f0f0f0;
                        margin: 5px 0;
                    }
                    .staff-info {
                        font-weight: bold;
                        font-size: 12px;
                        margin-top: 5px;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div><strong style="font-size:18px;">${hotelName}</strong></div>
                    <div style="font-weight:bold; font-size:14px;">Food & Beverage Service</div>
                    <div class="title">🧾 ORDER RECEIPT</div>
                    <div class="divider"></div>
                </div>
                
                <div>
                    <div class="order-detail"><strong>Order #:</strong> ${orderData.order_number}</div>
                    <div class="order-detail"><strong>Type:</strong> ${orderData.order_type_display || orderData.order_type}</div>
                    ${orderData.table_number ? `<div class="order-detail"><strong>Table:</strong> ${orderData.table_number}</div>` : ''}
                    ${orderData.room_number ? `<div class="order-detail"><strong>Room:</strong> ${orderData.room_number}${orderData.room_name ? ` (${orderData.room_name})` : ''}</div>` : ''}
                    ${orderData.guest_name && orderData.guest_name !== 'Walk-in Customer' ? `<div class="order-detail"><strong>Guest:</strong> ${orderData.guest_name}</div>` : ''}
                    <div class="order-detail"><strong>Date:</strong> ${date}</div>
                    <div class="status-badge"><strong>Status:</strong> ${orderData.status.toUpperCase()}</div>
                </div>
                
                <div class="divider"></div>
                
                <div>
                    <div class="item-header">
                        <span>QTY</span>
                        <span>ITEM</span>
                        <span>TOTAL</span>
                    </div>
                    ${orderData.items.map(item => `
                        <div class="item-row">
                            <span style="font-weight:900;">${item.quantity}x</span>
                            <span style="font-weight:bold;">${item.item_name.substring(0, 25)}</span>
                            <span style="font-weight:900;">₦${(item.quantity * item.unit_price).toFixed(2)}</span>
                        </div>
                    `).join('')}
                </div>
                
                <div class="divider-thick"></div>
                
                <div>
                    <div class="total-row">
                        <span>Subtotal:</span>
                        <span style="font-weight:900;">₦${parseFloat(orderData.total_amount || 0).toFixed(2)}</span>
                    </div>
                    ${parseFloat(orderData.tax_amount || 0) > 0 ? `
                        <div class="total-row">
                            <span>Tax (${orderData.tax_rate || 0}%):</span>
                            <span style="font-weight:900;">₦${parseFloat(orderData.tax_amount || 0).toFixed(2)}</span>
                        </div>
                    ` : ''}
                    ${parseFloat(orderData.service_charge || 0) > 0 ? `
                        <div class="total-row">
                            <span>Service Charge (${orderData.service_charge_rate || 0}%):</span>
                            <span style="font-weight:900;">₦${parseFloat(orderData.service_charge || 0).toFixed(2)}</span>
                        </div>
                    ` : ''}
                </div>
                
                <div class="grand-total">
                    <span>TOTAL:</span>
                    <span>₦${parseFloat(orderData.final_amount || 0).toFixed(2)}</span>
                </div>
                
                ${orderData.special_instructions ? `
                    <div class="divider"></div>
                    <div style="font-weight:bold; margin:5px 0;">
                        <strong>📝 Special Instructions:</strong><br>
                        ${orderData.special_instructions}
                    </div>
                ` : ''}
                
                <div class="divider"></div>
                
                <div class="thank-you">Thank You! Please come again.</div>
                
                <div class="footer">
                    <span class="timestamp">${new Date().toLocaleString()}</span><br>
                    <span class="staff-info">Staff: ${staffName}</span><br>
                    ${hotelName} F&B System
                </div>
                
                <script>
                    window.onload = function() {
                        window.print();
                        setTimeout(function() { window.close(); }, 800);
                    }
                </script>
            </body>
            </html>
        `;
        
        this._print(printContent);
    }

    _print(content) {
        try {
            const printWindow = window.open('', '_blank', 'width=400,height=600');
            if (!printWindow) {
                console.error('Failed to open print window. Popup may be blocked.');
                alert('Please allow popups for this site to print tickets.');
                return;
            }
            printWindow.document.write(content);
            printWindow.document.close();
        } catch (error) {
            console.error('Error printing:', error);
            alert('Failed to print. Please try again or check your printer connection.');
        }
    }

    // Queue and print with enhanced error handling
    async printTicket(ticketData, type) {
        try {
            if (!ticketData || !ticketData.items || ticketData.items.length === 0) {
                console.error('Invalid ticket data:', ticketData);
                throw new Error('No items to print');
            }

            if (type === 'KOT') {
                this.printKOT(ticketData);
            } else if (type === 'BOT') {
                this.printBOT(ticketData);
            } else if (type === 'RECEIPT') {
                this.printReceipt(ticketData);
            } else {
                // Default to BOT if type not recognized
                this.printBOT(ticketData);
            }
            
            console.log(`✅ ${type} ticket printed successfully for order ${ticketData.order_number}`);
        } catch (error) {
            console.error('❌ Error printing ticket:', error);
            throw error;
        }
    }
}

export default new PrintingService();